import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CheckCircle2, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  postSendConsentNotificationEmailRequest,
  selectSessionAdvisor,
  selectClient,
  selectMaybeClient,
  selectMaybeClientPartner,
} from '@client/model';
import { ConsentEmailDialog } from './ConsentEmailDialog';
import { useConsentEmailPrompt } from './use-consent-email-prompt';
import type { ConsentEmailPromptTarget } from './use-consent-email-prompt';

type UserRole = 'advisor' | 'client' | 'partnerClient';
type ConsentType = 'start' | 'submit';

interface ReminderResponse {
  reminded: boolean;
  remindedAtRelative: string;
  canRemindAgain: boolean;
}

interface ReminderStatusState {
  [key: string]: ReminderResponse;
}

interface ConsentStatusProps {
  clientPolicyApplicationId: string;
  consentStatusData: ApplicationConsentStatusResponse | null;
  currentUser: {
    type: 'client' | 'advisor' | 'partnerClient';
    isPending: boolean;
    userId: string;
  } | null;
  consentType: ConsentType;
}

interface ConsentRecordProps {
  user: UserInfo;
  consent: ConsentStatus;
  reminderStatus: ReminderResponse;
  currentUser: ConsentStatusProps['currentUser'];
  consentType: ConsentType;
  clientPolicyApplicationId: string;
  clientRecord: Client | null;
  onReminderSent: (userType: UserRole) => void;
  onRequestEmail: (target: ConsentEmailPromptTarget) => void;
}

interface UserInfo {
  key: string;
  name: string;
  role: string;
  userType: UserRole;
  isPending: boolean;
  isCurrentUser: boolean;
}

interface ReminderStatusDisplayProps {
  reminderStatus: ReminderResponse;
  userType: UserRole;
  currentUserType: UserRole;
}

const STATUS_CONFIG = {
  pending: {
    className:
      'inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-sm font-semibold leading-5 text-orange-800',
    icon: Clock,
    iconClassName: 'mr-2 h-4 w-4 text-orange-500',
    text: 'Pending',
  },
  completed: {
    className:
      'inline-flex items-center rounded-full bg-green-100 px-2 py-1 text-sm leading-5 text-green-800',
    icon: CheckCircle2,
    iconClassName: 'mr-2 h-4 w-4 text-green-500',
    text: (userType: UserRole, consentType: ConsentType) =>
      userType === 'advisor' && consentType === 'start' ? 'Not required' : 'Provided',
  },
};

const ACTION_CONFIG = {
  start: {
    client: {
      hasAction: true,
      requestType: 'remindClientForStartConsent' as SendConsentNotificationEmailRequestType,
      actionText: {
        canSend: 'Send Link',
        sent: 'Link Sent',
        completed: 'Consent Received',
      },
    },
    partnerClient: {
      hasAction: true,
      requestType: 'remindPartnerClientForStartConsent' as SendConsentNotificationEmailRequestType,
      actionText: {
        canSend: 'Send Link',
        sent: 'Link Sent',
        completed: 'Consent Received',
      },
    },
    advisor: {
      hasAction: false,
      requestType: null,
      actionText: {
        canSend: 'Not Applicable',
        sent: 'Not Applicable',
        completed: 'Not Applicable',
        noAction: 'Not Applicable',
      },
    },
  },
  submit: {
    client: {
      hasAction: true,
      requestType: 'remindClient' as SendConsentNotificationEmailRequestType,
      actionText: {
        canSend: 'Send Reminder',
        sent: 'Reminder Sent',
        completed: 'Consent Received',
      },
    },
    partnerClient: {
      hasAction: true,
      requestType: 'remindPartnerClient' as SendConsentNotificationEmailRequestType,
      actionText: {
        canSend: 'Send Reminder',
        sent: 'Reminder Sent',
        completed: 'Consent Received',
      },
    },
    advisor: {
      hasAction: true,
      requestType: null,
      actionText: {
        canSend: 'Complete Consent',
        sent: 'Consent Complete',
        completed: 'Consent Received',
      },
    },
  },
};

const getRelativeTimeString = (timestamp: string | null): string => {
  if (!timestamp) return '';

  try {
    const now = new Date();
    const remindedTime = new Date(timestamp);

    if (isNaN(remindedTime.getTime())) {
      console.warn('Invalid timestamp format:', timestamp);
      return '';
    }

    const diffInSeconds = Math.floor((now.getTime() - remindedTime.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return 'just now';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  } catch (error) {
    return '';
  }
};

export const getReminderStatus = (consentStatus: ConsentStatus): ReminderResponse => {
  const reminderResponse: ReminderResponse = {
    reminded: false,
    remindedAtRelative: '',
    canRemindAgain: true,
  };

  if (consentStatus.lastRemindedAt) {
    reminderResponse.reminded = true;
    reminderResponse.remindedAtRelative = getRelativeTimeString(consentStatus.lastRemindedAt);
    // Only disallow reminding again if lastRemindedAt is within 3 minutes
    const now = new Date();
    const remindedTime = new Date(consentStatus.lastRemindedAt);
    const diffInMs = now.getTime() - remindedTime.getTime();
    const diffInMinutes = diffInMs / (1000 * 60);
    reminderResponse.canRemindAgain = diffInMinutes >= 3;
  }

  return reminderResponse;
};

const ReminderStatusDisplay: React.FC<ReminderStatusDisplayProps> = ({
  reminderStatus,
  userType,
  currentUserType,
}) => {
  const shouldShow =
    currentUserType !== userType && reminderStatus.reminded && reminderStatus.remindedAtRelative;

  if (!shouldShow) return null;

  return (
    <div className="absolute ml-0.5 text-xs text-gray-700">
      Last sent {reminderStatus.remindedAtRelative}
    </div>
  );
};

const ConsentRecord: React.FC<ConsentRecordProps> = ({
  user,
  consent,
  reminderStatus,
  currentUser,
  consentType,
  clientPolicyApplicationId,
  clientRecord,
  onReminderSent,
  onRequestEmail,
}) => {
  const dispatch = useDispatch();

  const canSendReminder = user.isPending && !user.isCurrentUser && reminderStatus.canRemindAgain;

  const actionConfig = ACTION_CONFIG[consentType][user.userType];
  const statusConfig = user.isPending ? STATUS_CONFIG.pending : STATUS_CONFIG.completed;
  const StatusIcon = statusConfig.icon;

  const handleReminderClick = useCallback(() => {
    if (!canSendReminder || !actionConfig.requestType) return;

    if (clientRecord && !clientRecord.clientEmail) {
      onRequestEmail({
        client: clientRecord,
        isPrimaryClient: user.userType === 'client',
        requestType: actionConfig.requestType,
        consentType,
        clientPolicyApplicationId,
        onSent: () => onReminderSent(user.userType),
      });
      return;
    }

    dispatch(
      postSendConsentNotificationEmailRequest({
        clientPolicyApplicationId,
        requestType: actionConfig.requestType,
        consentType,
      }),
    );

    onReminderSent(user.userType);
  }, [
    canSendReminder,
    actionConfig.requestType,
    clientRecord,
    onRequestEmail,
    dispatch,
    clientPolicyApplicationId,
    consentType,
    onReminderSent,
    user.userType,
  ]);

  const getStatusText = () => {
    if (typeof statusConfig.text === 'function') {
      return statusConfig.text(user.userType, consentType);
    }
    return statusConfig.text;
  };

  const getActionText = () => {
    if (!user.isPending) {
      return actionConfig.actionText.completed;
    }
    return canSendReminder ? actionConfig.actionText.canSend : actionConfig.actionText.sent;
  };

  const isAdvisorAction = currentUser?.type === 'advisor';

  return (
    <tr className="hover:bg-gray-50">
      <td className="whitespace-nowrap px-6 py-4">
        <div>
          <div className="text-sm font-medium text-gray-900">
            {user.name}
            {user.isCurrentUser && <span className="ml-1 text-sm text-gray-700">(You)</span>}
          </div>
          <div className="text-sm text-gray-700">{user.role}</div>
        </div>
      </td>
      <td className="whitespace-nowrap px-6 py-4">
        <div className="flex items-center">
          <span className={statusConfig.className}>
            <StatusIcon className={statusConfig.iconClassName} />
            {getStatusText()}
          </span>
        </div>
      </td>
      {isAdvisorAction && (
        <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
          {actionConfig.hasAction && actionConfig.requestType && user.isPending ? (
            <>
              <Button
                size="sm"
                variant="outline"
                disabled={!canSendReminder}
                onClick={handleReminderClick}
                className="text-sm"
              >
                {getActionText()}
              </Button>
              <ReminderStatusDisplay
                reminderStatus={reminderStatus}
                userType={user.userType}
                currentUserType={currentUser?.type || 'client'}
              />
            </>
          ) : (
            <span className="text-sm text-gray-700">{getActionText()}</span>
          )}
        </td>
      )}
    </tr>
  );
};

const ConsentStatus: React.FC<ConsentStatusProps> = ({
  consentStatusData,
  currentUser,
  clientPolicyApplicationId,
  consentType,
}) => {
  const advisor = useSelector(selectSessionAdvisor);
  const client = useSelector(selectClient);
  const rawClient = useSelector(selectMaybeClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const emailPrompt = useConsentEmailPrompt();

  const [reminderStatuses, setReminderStatuses] = useState<ReminderStatusState>({});

  useEffect(() => {
    if (consentStatusData) {
      const newReminderStatuses: ReminderStatusState = {};
      Object.entries(consentStatusData.allConsents).forEach(([userType, consent]) => {
        if (consent) {
          newReminderStatuses[userType] = getReminderStatus(consent);
        }
      });
      setReminderStatuses(newReminderStatuses);
    }
  }, [consentStatusData]);

  const handleReminderSent = useCallback((userType: UserRole) => {
    setReminderStatuses(prev => ({
      ...prev,
      [userType]: {
        reminded: true,
        remindedAtRelative: 'just now',
        canRemindAgain: false,
      },
    }));
  }, []);

  if (!consentStatusData) return null;

  const users: UserInfo[] = [
    {
      key: 'advisor',
      name: `${advisor.advisorFirstName} ${advisor.advisorLastName}`,
      role: 'Advisor',
      userType: 'advisor',
      isPending: consentStatusData.allConsents.advisor.isPending,
      isCurrentUser: currentUser?.type === 'advisor',
    },
    {
      key: 'client',
      name: client.clientFullName,
      role: 'Primary client',
      userType: 'client',
      isPending: consentStatusData.allConsents.client.isPending,
      isCurrentUser: currentUser?.type === 'client',
    },
  ];

  if (consentStatusData.allConsents.partnerClient) {
    users.push({
      key: 'partnerClient',
      name: clientPartner?.clientFullName ?? 'Partner client',
      role: 'Partner client',
      userType: 'partnerClient',
      isPending: consentStatusData.allConsents.partnerClient.isPending,
      isCurrentUser: currentUser?.type === 'partnerClient',
    });
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {/* Mobile scroll container */}
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 sm:px-6 sm:py-3">
                  User Name
                </th>
                <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 sm:px-6 sm:py-3">
                  Consent Status
                </th>
                {currentUser?.type === 'advisor' && (
                  <th className="px-4 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-700 sm:px-6 sm:py-3">
                    Action
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {users.map(user => {
                const consent = consentStatusData?.allConsents[user.userType as keyof AllConsents];
                const reminderStatus =
                  reminderStatuses[user.userType] || getReminderStatus(consent!);

                const clientRecord =
                  user.userType === 'client'
                    ? rawClient
                    : user.userType === 'partnerClient'
                      ? clientPartner
                      : null;

                return (
                  <ConsentRecord
                    key={user.key}
                    user={user}
                    consent={consent!}
                    reminderStatus={reminderStatus}
                    currentUser={currentUser}
                    consentType={consentType}
                    clientPolicyApplicationId={clientPolicyApplicationId}
                    clientRecord={clientRecord}
                    onReminderSent={handleReminderSent}
                    onRequestEmail={emailPrompt.openPrompt}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <ConsentEmailDialog
        open={emailPrompt.isOpen}
        onOpenChange={open => {
          if (!open) emailPrompt.closePrompt();
        }}
        clientName={emailPrompt.clientName}
        isSubmitting={emailPrompt.isSubmitting}
        onSubmit={emailPrompt.submitEmail}
      />
    </div>
  );
};

export default ConsentStatus;
